import { PopoverOrigin } from '@mui/material';
import {
  RoutalSelect,
  RoutalSelectMultiSelectOption,
  RoutalSelectMultiSelectOptions,
  RoutalSelectProps,
  RoutalText as Text,
} from '@nx-smartmonkey/ui';
import { useState } from 'react';

interface TimeZoneSelectorProps extends Partial<RoutalSelectProps> {
  onChangeTimeZone: (value: TimeZone) => void;
  timeZone: TimeZone;
  listItemStyles?: React.CSSProperties;
  popoverProps?: {
    options: RoutalSelectMultiSelectOptions;
    maxHeight?: string;
    anchor?: {
      anchorOrigin?: PopoverOrigin;
      transformOrigin?: PopoverOrigin;
    };
    onOpen?: (event: any) => void;
    onBlur?: (event: any) => void;
  };
}

export const i18n = {
  'Etc/GMT+12': `(GMT-12:00) International Date Line West`,
  'Pacific/Midway': `(GMT-11:00) Midway Island, Samoa`,
  'Pacific/Honolulu': `(GMT-10:00) Hawaii`,
  'America/Juneau': `(GMT-09:00) Alaska`,
  'America/Dawson': `(GMT-08:00) Pacific Time (US and Canada); Tijuana`,
  'America/Boise': `(GMT-07:00) Mountain Time (US and Canada)`,
  'America/Chihuahua': `(GMT-07:00) Chihuahua, La Paz, Mazatlan`,
  'America/Phoenix': `(GMT-07:00) Arizona`,
  'America/Chicago': `(GMT-06:00) Central Time (US and Canada)`,
  'America/Regina': `(GMT-06:00) Saskatchewan`,
  'America/Mexico_City': `(GMT-06:00) Guadalajara, Mexico City, Monterrey`,
  'America/Belize': `(GMT-06:00) Central America`,
  'America/Detroit': `(GMT-05:00) Eastern Time (US and Canada)`,
  'America/Indiana/Indianapolis': `(GMT-05:00) Indiana (East)`,
  'America/Bogota': `(GMT-05:00) Bogota, Lima, Quito`,
  'America/Glace_Bay': `(GMT-04:00) Atlantic Time (Canada)`,
  'America/Caracas': `(GMT-04:00) Caracas, La Paz`,
  'America/Santiago': `(GMT-04:00) Santiago`,
  'America/St_Johns': `(GMT-03:30) Newfoundland and Labrador`,
  'America/Sao_Paulo': `(GMT-03:00) Brasilia`,
  'America/Argentina/Buenos_Aires': `(GMT-03:00) Buenos Aires, Georgetown`,
  'America/Godthab': `(GMT-03:00) Greenland`,
  'Etc/GMT+2': `(GMT-02:00) Mid-Atlantic`,
  'Atlantic/Azores': `(GMT-01:00) Azores`,
  'Atlantic/Cape_Verde': `(GMT-01:00) Cape Verde Islands`,
  GMT: `(GMT+00:00) Dublin, Edinburgh, Lisbon, London`,
  'Africa/Casablanca': `(GMT+00:00) Casablanca, Monrovia`,
  'Atlantic/Canary': `(GMT+00:00) Canary Islands`,
  'Europe/Belgrade': `(GMT+01:00) Belgrade, Bratislava, Budapest, Ljubljana, Prague`,
  'Europe/Sarajevo': `(GMT+01:00) Sarajevo, Skopje, Warsaw, Zagreb`,
  'Europe/Brussels': `(GMT+01:00) Brussels, Copenhagen, Madrid, Paris`,
  'Europe/Amsterdam': `(GMT+01:00) Amsterdam, Berlin, Bern, Rome, Stockholm, Vienna`,
  'Africa/Algiers': `(GMT+01:00) West Central Africa`,
  'Europe/Bucharest': `(GMT+02:00) Bucharest`,
  'Africa/Cairo': `(GMT+02:00) Cairo`,
  'Europe/Helsinki': `(GMT+02:00) Helsinki, Kiev, Riga, Sofia, Tallinn, Vilnius`,
  'Europe/Athens': `(GMT+02:00) Athens, Istanbul, Minsk`,
  'Asia/Jerusalem': `(GMT+02:00) Jerusalem`,
  'Africa/Harare': `(GMT+02:00) Harare, Pretoria`,
  'Europe/Moscow': `(GMT+03:00) Moscow, St. Petersburg, Volgograd`,
  'Asia/Kuwait': `(GMT+03:00) Kuwait, Riyadh`,
  'Africa/Nairobi': `(GMT+03:00) Nairobi`,
  'Asia/Baghdad': `(GMT+03:00) Baghdad`,
  'Asia/Tehran': `(GMT+03:30) Tehran`,
  'Asia/Dubai': `(GMT+04:00) Abu Dhabi, Muscat`,
  'Asia/Baku': `(GMT+04:00) Baku, Tbilisi, Yerevan`,
  'Asia/Kabul': `(GMT+04:30) Kabul`,
  'Asia/Yekaterinburg': `(GMT+05:00) Ekaterinburg`,
  'Asia/Karachi': `(GMT+05:00) Islamabad, Karachi, Tashkent`,
  'Asia/Kolkata': `(GMT+05:30) Chennai, Kolkata, Mumbai, New Delhi`,
  'Asia/Kathmandu': `(GMT+05:45) Kathmandu`,
  'Asia/Dhaka': `(GMT+06:00) Astana, Dhaka`,
  'Asia/Colombo': `(GMT+05:30) Sri Jayawardenepura`,
  'Asia/Almaty': `(GMT+06:00) Almaty, Novosibirsk`,
  'Asia/Rangoon': `(GMT+06:30) Yangon Rangoon`,
  'Asia/Bangkok': `(GMT+07:00) Bangkok, Hanoi, Jakarta`,
  'Asia/Krasnoyarsk': `(GMT+07:00) Krasnoyarsk`,
  'Asia/Shanghai': `(GMT+08:00) Beijing, Chongqing, Hong Kong SAR, Urumqi`,
  'Asia/Kuala_Lumpur': `(GMT+08:00) Kuala Lumpur, Singapore`,
  'Asia/Taipei': `(GMT+08:00) Taipei`,
  'Australia/Perth': `(GMT+08:00) Perth`,
  'Asia/Irkutsk': `(GMT+08:00) Irkutsk, Ulaanbaatar`,
  'Asia/Seoul': `(GMT+09:00) Seoul`,
  'Asia/Tokyo': `(GMT+09:00) Osaka, Sapporo, Tokyo`,
  'Asia/Yakutsk': `(GMT+09:00) Yakutsk`,
  'Australia/Darwin': `(GMT+09:30) Darwin`,
  'Australia/Adelaide': `(GMT+09:30) Adelaide`,
  'Australia/Sydney': `(GMT+10:00) Canberra, Melbourne, Sydney`,
  'Australia/Brisbane': `(GMT+10:00) Brisbane`,
  'Australia/Hobart': `(GMT+10:00) Hobart`,
  'Asia/Vladivostok': `(GMT+10:00) Vladivostok`,
  'Pacific/Guam': `(GMT+10:00) Guam, Port Moresby`,
  'Asia/Magadan': `(GMT+11:00) Magadan, Solomon Islands, New Caledonia`,
  'Pacific/Fiji': `(GMT+12:00) Fiji Islands, Kamchatka, Marshall Islands`,
  'Pacific/Auckland': `(GMT+12:00) Auckland, Wellington`,
  'Pacific/Tongatapu': `(GMT+13:00) Nuku'alofa`,
};

// Define a type for i18n to ensure TypeScript knows about its shape
type I18nType = typeof i18n;

// Use keyof to create a type with all keys of i18n as possible options
export type TimeZone = keyof I18nType;

//TODO no se si hacer un estado none
export const getZoneByTimezone = (key: TimeZone) => {
  return i18n[key] || `(GMT+00:00) Dublin, Edinburgh, Lisbon, London`;
};

export const TimeZoneSelector = (props: TimeZoneSelectorProps) => {
  const settingsTimeZones = Object.entries(i18n).map(([value, label]) => ({
    value,
    label,
  }));
  const [timezone, setTimezone] = useState<{ value: string; label: string }>({
    value: props.timeZone ?? `GMT`,
    label: getZoneByTimezone(props.timeZone ?? `GMT`),
  });
  return (
    <RoutalSelect
      {...props}
      id="time-zone-select"
      popoverProps={{
        options: settingsTimeZones.map(
          (setting: { value: string; label: string }): RoutalSelectMultiSelectOption => ({
            key: setting.value,
            listItemStyles: props.listItemStyles,
            content: <Text style={{}}>{setting.label}</Text>,
            onClick: () => {
              // updateProject({ timeZone: setting.value });
              setTimezone(setting);
              props.onChangeTimeZone(setting.value as TimeZone);
            },
          })
        ),
      }}
      {...props.popoverProps}
    >
      <Text style={{ width: `100%`, textAlign: `start`, cursor: `pointer` }}>{timezone.label}</Text>
    </RoutalSelect>
  );
};
